import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle, ArrowRight, Globe, Zap, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const LandingPage = () => {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section - Above the Fold */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-secondary/50 to-background" data-testid="hero-section">
        <div className="container mx-auto">
          <div className="max-w-4xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-6">
              Scan Your Ads for Risk Before You Publish.
            </h1>
            <p className="text-xl text-muted-foreground mb-4 max-w-2xl">
              Instant AI-powered ad risk analysis across major platforms and regions. Identify potential policy and regulatory friction before your campaigns go live.
            </p>
            <p className="text-base text-muted-foreground mb-8 max-w-2xl">
              For marketers, agencies, and founders running paid ads.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-get-started-btn">
                <Button size="lg" className="text-lg px-8">
                  Start Free – 3 Risk Scans <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Product Hunt Badge Section */}
      <section className="py-10 md:py-6 px-6 bg-background" data-testid="producthunt-section">
        <div className="container mx-auto">
          <div className="max-w-full md:max-w-[800px] mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center gap-2 mb-3">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 40 40" 
                  className="h-8 w-8"
                  aria-label="Product Hunt"
                >
                  <circle cx="20" cy="20" r="20" fill="#DA552F"/>
                  <path d="M22.667 20H17.333V14.667H22.667C24.133 14.667 25.333 15.867 25.333 17.333C25.333 18.8 24.133 20 22.667 20ZM22.667 12H14.667V28H17.333V22.667H22.667C25.6 22.667 28 20.267 28 17.333C28 14.4 25.6 12 22.667 12Z" fill="white"/>
                </svg>
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">
                  Find ScanMyAd on Product Hunt
                </h2>
              </div>
              <p className="text-muted-foreground mb-5 text-sm sm:text-base">
                Discover and support ScanMyAd on Product Hunt.
              </p>
              <a 
                href="https://www.producthunt.com/posts/scanmyad" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="producthunt-link"
              >
                <Button variant="outline" size="default" className="gap-2">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 40 40" 
                    className="h-4 w-4"
                  >
                    <circle cx="20" cy="20" r="20" fill="#DA552F"/>
                    <path d="M22.667 20H17.333V14.667H22.667C24.133 14.667 25.333 15.867 25.333 17.333C25.333 18.8 24.133 20 22.667 20ZM22.667 12H14.667V28H17.333V22.667H22.667C25.6 22.667 28 20.267 28 17.333C28 14.4 25.6 12 22.667 12Z" fill="white"/>
                  </svg>
                  View us on Product Hunt
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 md:py-10 px-6 bg-muted/30 border-y" data-testid="seo-section">
        <div className="container mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-6 md:mb-4">
            How ScanMyAd Works
          </h2>
          <p className="text-base text-muted-foreground max-w-4xl mx-auto text-center leading-relaxed">
            ScanMyAd is an AI-powered advertising risk assessment tool that helps advertisers detect potential platform policy conflicts and regulatory sensitivities before publishing ads. The system reviews submitted copy against selected publicly available guidance (such as FTC advertising principles, Health Canada considerations, GDPR-related wording sensitivities, and major platform advertising policies) and generates a structured risk summary with suggested revisions. ScanMyAd does not provide legal advice.
          </p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-6" data-testid="problem-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">The Cost of Ad Policy Violations</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Platform enforcement is accelerating. One flagged ad can shut down campaigns, suspend accounts, or trigger regulatory scrutiny.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <AlertTriangle className="h-12 w-12 text-accent mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">Ad Account Suspensions</h3>
                <p className="text-muted-foreground">
                  Meta, Google, and TikTok use AI to detect policy issues. One mistake can lead to account restrictions or permanent suspension.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <AlertTriangle className="h-12 w-12 text-accent mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">Regulatory Scrutiny</h3>
                <p className="text-muted-foreground">
                  FTC, EU authorities, and Health Canada actively monitor advertising claims. Penalties and enforcement actions can be significant.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <AlertTriangle className="h-12 w-12 text-accent mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">Brand Reputation Risk</h3>
                <p className="text-muted-foreground">
                  Questionable claims damage trust. Public enforcement actions become permanent records that can harm brand credibility.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 px-6 bg-secondary/30" data-testid="solution-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">AI-Powered Ad Risk Assessment</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              ScanMyAd analyzes your advertising content against multi-jurisdiction guidance and platform policies before you publish.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card>
              <CardContent className="pt-6">
                <Globe className="h-12 w-12 text-primary mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">Multi-Jurisdiction Analysis</h3>
                <p className="text-muted-foreground">
                  Analyze risk indicators across Canada, US (FTC principles), EU (GDPR-related), and India against publicly available regulatory guidance.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Zap className="h-12 w-12 text-primary mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">Platform Policy Check</h3>
                <p className="text-muted-foreground">
                  Detect potential policy friction with Meta, Google, TikTok, Amazon, LinkedIn, and YouTube advertising policies before submission.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <FileText className="h-12 w-12 text-primary mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">Lower-Risk Suggested Revisions</h3>
                <p className="text-muted-foreground">
                  Get AI-generated lower-risk alternatives and suggested language adjustments to reduce potential friction with policies.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6" data-testid="how-it-works-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">How It Works</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2">Paste Your Ad Content</h3>
                <p className="text-muted-foreground">
                  Input your ad text, upload creatives, add landing page URLs, or paste funnel scripts.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2">Select Region & Platform</h3>
                <p className="text-muted-foreground">
                  Choose your target market (Canada, US, EU, India) and advertising platform (Meta, Google, TikTok, etc.).
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2">Get Your Risk Assessment Report</h3>
                <p className="text-muted-foreground">
                  Receive a detailed risk score, risk indicators by region and platform, and lower-risk suggested revisions.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/register" data-testid="how-it-works-cta">
              <Button size="lg" className="text-lg px-8">
                Start Scanning Ads <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 px-6 bg-secondary/30" data-testid="trust-section">
        <div className="container mx-auto">
          <div className="text-center">
            <Shield className="h-16 w-16 text-primary mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">Built for Transparency</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-2">
              Canadian incorporated company serving global advertisers.
            </p>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              BC Leads Business Services Ltd. | Unit 108, 13277 108 Avenue, Surrey, BC V3T 0A9, British Columbia, Canada
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6" data-testid="cta-section">
        <div className="container mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">Ready to Scan Your Ads?</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Start with 3 free risk scans. No credit card required.
          </p>
          <Link to="/register" data-testid="final-cta-btn">
            <Button size="lg" className="text-lg px-8">
              Start Free – 3 Risk Scans <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
